import { useCallback, useEffect, useRef, useState } from 'react'

import { useLatestCallback } from './useLatestCallback'

interface SpeechRecognitionEvent extends Event {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

interface SpeechRecognitionType extends EventTarget {
  abort: () => void
  continuous: boolean
  interimResults: boolean
  lang: string
  onend: (() => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  start: () => void
  stop: () => void
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionType
    webkitSpeechRecognition?: new () => SpeechRecognitionType
  }
}

export interface SpeechRecognitionConfig {
  autoStop: boolean
  onTranscriptComplete?: (transcript: string) => void
}

export interface UseSpeechRecognitionReturn {
  audioStream: MediaStream | null
  error: string | null
  isListening: boolean
  isSupported: boolean
  startListening: () => void
  stopListening: () => void
  transcript: string
}

/**
 * Custom hook for using the Web Speech API (SpeechRecognition)
 */
export const useSpeechRecognition = (
  config: SpeechRecognitionConfig,
): UseSpeechRecognitionReturn => {
  const { autoStop, onTranscriptComplete } = config

  // Check browser support
  const SpeechRecognitionAPI =
    window.SpeechRecognition || window.webkitSpeechRecognition

  const isSupported = Boolean(SpeechRecognitionAPI)

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)

  const recognitionRef = useRef<SpeechRecognitionType | null>(null)
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const finalizedTranscriptRef = useRef('')
  const isListeningRef = useRef(false)
  const onTranscriptCompleteRef = useLatestCallback(onTranscriptComplete)

  // Initialize SpeechRecognition instance
  useEffect(() => {
    if (!isSupported || !SpeechRecognitionAPI) return undefined

    const recognition = new SpeechRecognitionAPI()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = ''
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcriptPart = result[0].transcript

        if (result.isFinal) {
          finalTranscript += `${transcriptPart} `
        } else {
          interimTranscript += transcriptPart
        }
      }

      if (finalTranscript) {
        finalizedTranscriptRef.current =
          `${finalizedTranscriptRef.current} ${finalTranscript}`.trim()

        // Reset silence timer for auto-stop
        if (autoStop) {
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current)
          }
          // Stop after 2 seconds of silence
          silenceTimerRef.current = setTimeout(() => {
            recognition.stop()
          }, 2000)
        }
      }

      const displayTranscript = interimTranscript
        ? `${finalizedTranscriptRef.current} ${interimTranscript}`.trim()
        : finalizedTranscriptRef.current

      setTranscript(displayTranscript)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // eslint-disable-next-line no-console
      console.error('Speech recognition error:', event.error)

      if (event.error === 'not-allowed') {
        setError('microphone-denied')
      } else {
        setError(event.error)
      }

      setIsListening(false)
      isListeningRef.current = false
    }

    recognition.onend = () => {
      setIsListening(false)
      isListeningRef.current = false

      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }

      // Trigger callback with final transcript
      const finalText = finalizedTranscriptRef.current.trim()
      if (finalText && onTranscriptCompleteRef.current) {
        onTranscriptCompleteRef.current(finalText)
      }

      setAudioStream((prevStream) => {
        if (prevStream) {
          prevStream.getTracks().forEach((track) => track.stop())
        }
        return null
      })
    }

    recognitionRef.current = recognition

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
      }
    }
  }, [isSupported, autoStop, onTranscriptCompleteRef])

  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListening) return

    setError(null)
    setTranscript('')
    finalizedTranscriptRef.current = ''
    setIsListening(true)
    isListeningRef.current = true

    try {
      recognitionRef.current.start()

      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          if (!isListeningRef.current) {
            stream.getTracks().forEach((track) => track.stop())
            return
          }
          setAudioStream(stream)
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to get microphone access for visualization:',
            err,
          )
        })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to start speech recognition:', err)
      setIsListening(false)
      isListeningRef.current = false
      setError('start-failed')
    }
  }, [isListening])

  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) return

    isListeningRef.current = false
    recognitionRef.current.stop()

    setAudioStream((prevStream) => {
      if (prevStream) {
        prevStream.getTracks().forEach((track) => track.stop())
      }
      return null
    })
  }, [isListening])

  return {
    audioStream,
    error,
    isListening,
    isSupported,
    startListening,
    stopListening,
    transcript,
  }
}
